// src/helpers/getSphereById.ts

import { ApiClient } from '@center-technologies/shared-api-clients';

interface SphereByIdResult {
  success: boolean;
  statusCode: number;
  message: string;
  data: any;
}

/**
 * Short-lived shared cache for full-sphere reads, plus in-flight coalescing.
 *
 * `globalSphereCache` covers components that only need sphere *info*; this covers
 * the ones that legitimately need the whole vertex (assigned perspectives, expanded
 * arcs, `healthState`) and so cannot use the info projection. Those callers are
 * independent — app initialisation, the metaspace page, the sphere editor — with no
 * shared React state to hang a fetch off, so each one issued its own request for the
 * same sphere. On a page whose URL sphere IS the signed-in user's own sphere that
 * meant two identical `GET /spheres/:id` calls a second apart.
 *
 * The TTL is deliberately short. It exists to collapse the burst of reads that a
 * single page assembly produces, not to keep sphere data around: anything longer
 * would start serving edits back stale. Mutations invalidate explicitly via
 * `invalidateSphereById`, and `skipCache` callers bypass it entirely.
 */
const SPHERE_TTL_MS = 5000;

interface CachedSphere {
  result: SphereByIdResult;
  expiresAt: number;
}

/**
 * Cache entries are scoped to the `ApiClient` that produced them. Two clients can be
 * live at once (a re-auth or an impersonation swap hands out a new one), and they do
 * not necessarily speak for the same identity or endpoint — so a key without the
 * client would let one client read the other's sphere for up to the TTL. Ids come
 * from a WeakMap so a discarded client's entries are collectable, while the cache
 * itself stays a flat Map that `invalidateSphereById` can still scan by sphere.
 */
const clientIds = new WeakMap<ApiClient, number>();
let nextClientId = 0;

function clientKey(apiClient: ApiClient): number {
  let id = clientIds.get(apiClient);
  if (id === undefined) {
    id = nextClientId++;
    clientIds.set(apiClient, id);
  }
  return id;
}

const sphereCache = new Map<string, CachedSphere>();
const sphereInflight = new Map<string, Promise<SphereByIdResult>>();

/**
 * Drop cached full-sphere reads for a sphere (all spheres when omitted). Called by
 * every sphere-mutating helper in this package so an edit is never read back stale.
 */
export function invalidateSphereById(sphereId?: string): void {
  if (!sphereId) {
    sphereCache.clear();
    return;
  }
  for (const key of sphereCache.keys()) {
    if (key.split('|')[1] === sphereId) sphereCache.delete(key);
  }
}

/**
 * Retrieves a sphere by its ID from the /spheres route.
 *
 * @param apiClient – An initialized ApiClient pointed at your Graph endpoint.
 * @param sphereId – The ID of the sphere to retrieve.
 * @param getAssignedPerspectives – Whether to include assigned perspectives.
 * @param skipCache – If true, bypass cache and fetch fresh data.
 * @returns An object with success, statusCode, message, and data fields.
 * @throws Error object with { success, statusCode, message, response } on failure. `statusCode` is the
 * status graph-service answered; a request it sent that got no answer (refused, reset, timed out) is
 * 503, so a caller can retry it; a request that was never sent (it could not be built or authorised)
 * or that the caller cancelled is 500.
 */
export async function getSphereById(
  apiClient: ApiClient,
  sphereId: string,
  getAssignedPerspectives = false,
  skipCache = false,
  expandArcs = false
): Promise<SphereByIdResult> {
  if (!sphereId.trim()) {
    throw {
      success: false,
      statusCode: 400,
      message: 'getSphereById: sphereId is required',
      response: null,
    };
  }

  // Build URL path and optional query string
  const params: string[] = [];
  if (getAssignedPerspectives) {
    params.push('getAssignedPerspectives=true');
  }
  if (skipCache) {
    params.push('skipCache=true');
  }
  if (expandArcs) {
    params.push('expandArcs=true');
  }
  const qs = params.length > 0 ? `?${params.join('&')}` : '';
  const path = `/spheres/${encodeURIComponent(sphereId)}${qs}`;

  // `skipCache` is the caller saying "I need this fresh" — honour it on the way in
  // (drop what we hold) as well as on the way out (the query string the server sees).
  const cacheKey = `${clientKey(apiClient)}|${sphereId}|${getAssignedPerspectives}|${expandArcs}`;
  if (skipCache) {
    sphereCache.delete(cacheKey);
  } else {
    const cached = sphereCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.result;
    }
    const pending = sphereInflight.get(cacheKey);
    if (pending) return pending;
  }

  const request = fetchSphere(apiClient, path, sphereId).then((result) => {
    sphereCache.set(cacheKey, { result, expiresAt: Date.now() + SPHERE_TTL_MS });
    return result;
  });

  if (!skipCache) {
    sphereInflight.set(cacheKey, request);
  }
  try {
    return await request;
  } finally {
    if (sphereInflight.get(cacheKey) === request) {
      sphereInflight.delete(cacheKey);
    }
  }
}

async function fetchSphere(
  apiClient: ApiClient,
  path: string,
  sphereId: string
): Promise<SphereByIdResult> {
  try {
    // Coalescing and caching happen in getSphereById, keyed per client; this is the
    // one place that actually reaches the network.
    const data = await apiClient.get<any>(path);
    const retObj = {
      success: true,
      statusCode: 200,
      message: 'Sphere retrieved successfully',
      data,
    };
    return retObj;
  } catch (err: any) {
    // If AxiosError-like structure, err.response may exist
    const sentWithoutAnswer = !err.response && Boolean(err.request) && err.code !== 'ERR_CANCELED';
    const status = err.response?.status ?? (sentWithoutAnswer ? 503 : 500);
    const errorMsg =
      err.response?.data?.error ?? err.message ?? 'Failed to retrieve sphere';

    console.error(`getSphereById error for sphereId=${sphereId}: ${status} - ${errorMsg}`);

    throw {
      success: false,
      statusCode: status,
      message: errorMsg,
      response: err.response ?? { status, data: { error: errorMsg } },
    };
  }
}
